import React from 'react';

import { pollForElement } from 'src/utils/DomUtils';

// Backstop (~1s at 60fps) so an anchor that never fully stops moving still gets
// shown eventually, rather than spinning requestAnimationFrame forever.
const MAX_SETTLE_FRAMES = 60;

type AnchorResolver = () => HTMLElement | null;

// The Popper measures its anchor only once, so an anchor that is still animating
// into place (e.g. a menu opening with a Grow transition) would be measured at a
// transient position and the card would end up misaligned. Consider the anchor
// settled once its rect is unchanged between two consecutive animation frames.
const isSameRect = (a: DOMRect, b: DOMRect): boolean =>
  a.top === b.top &&
  a.left === b.left &&
  a.width === b.width &&
  a.height === b.height;

/**
 * Resolves a DOM element from a CSS selector or a resolver function. Polls briefly
 * because the target (e.g. an AG Grid row or a menu mid-open) can render a few
 * frames after the step activates, then waits for the element to stop moving
 * before returning it, so the Popper anchors to its final rect. Once resolved it
 * keeps watching: if the element leaves the DOM (e.g. a menu the tour opened is
 * dismissed) it drops the anchor and resolves again, so the Popper never measures
 * a detached node. Returns null until settled, or if `target` is null. Pass a
 * memoized resolver so the effect stays stable.
 */
export function useResolvedAnchor(
  target: string | AnchorResolver | null,
): HTMLElement | null {
  const [element, setElement] = React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    // Reset when the target changes so a stale anchor never shows between steps.
    setElement(null);
    if (!target) {
      return undefined;
    }

    let cancelled = false;
    let frameId: number | undefined;
    let cancelPoll: (() => void) | undefined;

    const resolveOnce = (): HTMLElement | null => {
      if (typeof target === 'string') {
        const found = document.querySelector(target);
        return found instanceof HTMLElement ? found : null;
      }
      return target();
    };

    // A detached node reports an all-zero rect, which throws the Popper into the
    // top-left corner. Once the resolved anchor leaves the DOM, drop it and poll
    // again for a replacement (e.g. the same menu item after the menu reopens).
    const watchForDetach = (el: HTMLElement) => {
      frameId = requestAnimationFrame(() => {
        if (cancelled) {
          return;
        }
        if (el.isConnected) {
          watchForDetach(el);
          return;
        }
        setElement(null);
        startPolling();
      });
    };

    // Return the anchor only once its position has stabilized (see isSameRect),
    // or once the settle budget runs out (see MAX_SETTLE_FRAMES).
    const settleThenSet = (
      el: HTMLElement,
      previous: DOMRect,
      framesLeft: number,
    ) => {
      frameId = requestAnimationFrame(() => {
        if (cancelled) {
          return;
        }
        const current = el.getBoundingClientRect();
        if (framesLeft <= 0 || isSameRect(previous, current)) {
          setElement(el);
          watchForDetach(el);
          return;
        }
        settleThenSet(el, current, framesLeft - 1);
      });
    };

    function startPolling(): void {
      cancelPoll = pollForElement(resolveOnce, (el) => {
        settleThenSet(el, el.getBoundingClientRect(), MAX_SETTLE_FRAMES);
      });
    }

    startPolling();

    return () => {
      cancelled = true;
      cancelPoll?.();
      if (frameId !== undefined) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [target]);

  return element;
}
